import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import servicesData from "../../data/servicesData";

import "./Services.css";

function Services() {
  const services = Object.entries(servicesData);

  const servicesSectionRef = useRef(null);
  const [servicesVisible, setServicesVisible] = useState(false);

  /*
    =========================================================
    SERVICES SCROLL ANIMATION
    Animation starts when around 10% of the section
    enters the viewport.
    =========================================================
  */

  useEffect(() => {
    const section = servicesSectionRef.current;

    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setServicesVisible(true);

          observer.unobserve(section);
        }
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="services-page">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="services-hero">

        {/* Technical background elements */}

        <div className="services-hero-grid"></div>

        <div className="services-hero-glow services-hero-glow-one"></div>

        <div className="services-hero-glow services-hero-glow-two"></div>

        <div className="services-hero-orbit"></div>


        <div className="services-container">

          <div className="services-hero-content">

            <div className="services-hero-label">
              <span></span>
              WHAT WE DO
            </div>

            <h1>
              Digital solutions
              <br />
              <span>built around your business.</span>
            </h1>

            <p>
              From modern websites and software applications to AI
              solutions and automation, Nexis Fold builds practical
              technology designed around your business requirements.
            </p>

            <div className="services-hero-actions">

              <Link
                to="/contact"
                className="services-hero-button"
              >
                Start a Project
                <ArrowRight size={16} />
              </Link>

              <a
                href="#our-services"
                className="services-hero-link"
              >
                Explore services
                <ArrowUpRight size={16} />
              </a>

            </div>

          </div>


          <div className="services-hero-info">

            <div className="services-hero-info-number">
              {String(services.length).padStart(2, "0")}
            </div>

            <div className="services-hero-info-line"></div>

            <p>
              Core Technology
              <br />
              Services
            </p>

          </div>


          {/* Hero technical visual */}

          <div className="services-hero-tech">

            <div className="services-tech-frame">

              <div className="services-tech-corner services-tech-corner-tl"></div>
              <div className="services-tech-corner services-tech-corner-tr"></div>
              <div className="services-tech-corner services-tech-corner-bl"></div>
              <div className="services-tech-corner services-tech-corner-br"></div>

              <div className="services-tech-ring services-tech-ring-one"></div>

              <div className="services-tech-ring services-tech-ring-two"></div>

              <div className="services-tech-core">
                <span></span>
              </div>

              <div className="services-tech-line services-tech-line-one"></div>

              <div className="services-tech-line services-tech-line-two"></div>

              <div className="services-tech-status">
                <span></span>
                DIGITAL SYSTEMS
              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          SERVICES LIST
      ===================================================== */}

      <section
        id="our-services"
        ref={servicesSectionRef}
        className={`services-list-section ${
          servicesVisible ? "services-visible" : ""
        }`}
      >

        {/* Background technology grid */}

        <div className="services-section-grid"></div>

        <div className="services-container">

          <div className="services-section-heading">

            <div>

              <span className="services-label">
                OUR CAPABILITIES
              </span>

              <h2>
                Technology with
                <br />
                <span>purpose.</span>
              </h2>

            </div>


            <div className="services-section-heading-right">

              <p>
                We combine design, engineering and modern technologies
                to create digital solutions that are useful, reliable
                and built around real business requirements.
              </p>

              <div className="services-heading-line">
                <span></span>
              </div>

            </div>

          </div>


          {/* =================================================
              SERVICE GRID
          ================================================= */}

          <div className="services-grid">

            {services.map(([slug, service], index) => {

              const Icon = service.icon;

              return (

                <article
                  className={`service-card service-card-${index + 1}`}
                  key={slug}
                >

                  {/* Technical card background */}

                  <div className="service-card-grid"></div>

                  <div className="service-card-glow"></div>

                  <div className="service-card-beam"></div>


                  {/* Card top */}

                  <div className="service-card-top">

                    <span className="service-number">
                      {service.number}
                    </span>

                    <div className="service-icon">

                      <Icon
                        size={23}
                        strokeWidth={1.7}
                      />

                    </div>

                  </div>


                  {/* Small technical indicator */}

                  <div className="service-card-indicator">

                    <span></span>

                    <span></span>

                    <span></span>

                  </div>


                  {/* Card content */}

                  <div className="service-card-content">

                    <h3>
                      {service.title}
                    </h3>

                    <p>
                      {service.description}
                    </p>

                  </div>


                  {/* Explore */}

                  <Link
                    to={`/services/${slug}`}
                    className="explore-service"
                  >

                    <span>
                      Explore Service
                    </span>

                    <div className="explore-service-arrow">
                      <ArrowUpRight size={15} />
                    </div>

                  </Link>

                </article>

              );

            })}

          </div>


          {/* Bottom technical information */}

          <div className="services-grid-footer">

            <div className="services-grid-footer-line"></div>

            <span>
              SOFTWARE · AI · AUTOMATION · DIGITAL PRODUCTS
            </span>

            <div className="services-grid-footer-line"></div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="services-cta">

        <div className="services-cta-grid"></div>

        <div className="services-container">

          <div className="services-cta-box">

            <div className="services-cta-glow"></div>

            <div className="services-cta-content">

              <span className="services-label">
                HAVE A PROJECT IN MIND?
              </span>

              <h2>
                Let's build something
                <br />
                <span>meaningful.</span>
              </h2>

              <p>
                Tell us what you're trying to build and we'll
                help turn the idea into a practical digital
                solution.
              </p>

            </div>


            <Link
              to="/contact"
              className="services-cta-button"
            >

              <span>
                Start a Project
              </span>

              <ArrowUpRight size={17} />

            </Link>

          </div>

        </div>

      </section>

    </div>
  );
}

export default Services;